<template>
  <teleport to="body">
  <transition name="lightbox-fade">
    <div v-if="visible" class="image-lightbox-overlay" @click.self="close" @wheel.prevent="onWheel">
      <button class="lightbox-close-btn" @click="close" aria-label="Close">
        <i class="fa fa-times"></i>
      </button>
      <div class="lightbox-toolbar">
        <button @click="zoomOut" aria-label="Zoom out"><i class="fa fa-search-minus"></i></button>
        <button @click="resetZoom" aria-label="Reset zoom"><i class="fa fa-refresh"></i></button>
        <button @click="zoomIn" aria-label="Zoom in"><i class="fa fa-search-plus"></i></button>
      </div>
      <div class="lightbox-image-wrap" @mousedown="startDrag" @mousemove="onDrag" @mouseup="endDrag" @mouseleave="endDrag">
        <img
          :src="src"
          class="lightbox-image"
          :class="{ zoomed: scale > 1, dragging: isDragging }"
          :style="imageStyle"
          @click.stop="toggleZoom"
          draggable="false"
        />
      </div>
    </div>
  </transition>
  </teleport>
</template>

<script>
export default {
  name: 'ImageLightbox',
  props: {
    visible: { type: Boolean, default: false },
    src: { type: String, default: '' }
  },
  emits: ['close'],
  data() {
    return {
      scale: 1,
      offsetX: 0,
      offsetY: 0,
      isDragging: false,
      dragMoved: false,
      dragStartX: 0,
      dragStartY: 0
    }
  },
  computed: {
    imageStyle() {
      return {
        transform: `translate(${this.offsetX}px, ${this.offsetY}px) scale(${this.scale})`,
        cursor: this.scale > 1 ? (this.isDragging ? 'grabbing' : 'grab') : 'zoom-in'
      }
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.resetZoom()
        document.addEventListener('keydown', this.onKeydown)
      } else {
        document.removeEventListener('keydown', this.onKeydown)
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    close() {
      this.$emit('close')
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.close()
    },
    toggleZoom() {
      if (this.dragMoved) {
        this.dragMoved = false
        return
      }
      if (this.scale > 1) {
        this.resetZoom()
      } else {
        this.scale = 2
      }
    },
    zoomIn() {
      this.scale = Math.min(this.scale + 0.5, 4)
    },
    zoomOut() {
      this.scale = Math.max(this.scale - 0.5, 1)
      if (this.scale === 1) this.resetOffset()
    },
    resetZoom() {
      this.scale = 1
      this.resetOffset()
    },
    resetOffset() {
      this.offsetX = 0
      this.offsetY = 0
    },
    onWheel(e) {
      if (e.deltaY < 0) {
        this.zoomIn()
      } else {
        this.zoomOut()
      }
    },
    startDrag(e) {
      if (this.scale <= 1) return
      this.isDragging = true
      this.dragMoved = false
      this.dragStartX = e.clientX - this.offsetX
      this.dragStartY = e.clientY - this.offsetY
    },
    onDrag(e) {
      if (!this.isDragging) return
      var newX = e.clientX - this.dragStartX
      var newY = e.clientY - this.dragStartY
      if (Math.abs(newX - this.offsetX) > 3 || Math.abs(newY - this.offsetY) > 3) {
        this.dragMoved = true
      }
      this.offsetX = newX
      this.offsetY = newY
    },
    endDrag() {
      this.isDragging = false
    }
  }
}
</script>

<style scoped>
.image-lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
}
.lightbox-close-btn {
  position: absolute;
  top: 20px;
  right: 24px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.65);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
  color: #fff;
  font-size: 18px;
  cursor: pointer;
  z-index: 2002;
}
.lightbox-close-btn:hover {
  background: rgba(0, 0, 0, 0.85);
}
.lightbox-toolbar {
  position: absolute;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  z-index: 2002;
}
.lightbox-toolbar button {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.3);
  background: rgba(0, 0, 0, 0.65);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.5);
  color: #fff;
  cursor: pointer;
}
.lightbox-toolbar button:hover {
  background: rgba(0, 0, 0, 0.85);
}
.lightbox-image-wrap {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}
.lightbox-image {
  max-width: 90vw;
  max-height: 90vh;
  transition: transform 0.15s ease;
  user-select: none;
}
.lightbox-image.dragging {
  transition: none;
}
.lightbox-fade-enter-active,
.lightbox-fade-leave-active {
  transition: opacity 0.2s ease;
}
.lightbox-fade-enter-from,
.lightbox-fade-leave-to {
  opacity: 0;
}
</style>
